// 대시보드 #/ — 오늘 숫자 + 곧 올 학생 + 챙겨야 할 학생(v0.3 Home.kt). 배치는 SCREENS §1-2
import { currentMe } from "../data/actions";
import { itemsFor, openItems, sessionOrder, stats, studentMap, upcoming, warnsOf } from "../data/select";
import { useView } from "../data/store";
import { St } from "../data/types";
import { DOW, dowValue, minusMinutesHM, parseYmd, todayStr } from "../lib/date";
import { useWidthClass } from "../ui/hooks";
import { Icon } from "../ui/icons";
import { Empty, Pill, Section, StatTile, StudentName } from "../ui/parts";

const LIMIT = 5;

export function Dashboard() {
  const v = useView();
  const wc = useWidthClass();
  const me = currentMe();
  const td = todayStr();
  const d = parseYmd(td);
  const sm = studentMap(v);
  const stt = stats(v, td);
  const todays = v.sessions.filter((s) => s.date === td && sm.has(s.studentId)).sort(sessionOrder);
  const cut = minusMinutesHM(30);
  const next = todays.filter((s) => s.status === St.PLAN && s.time >= cut);
  const th = v.settings.warnThreshold;
  const warnHeavy = v.students.map((s) => ({ s, n: warnsOf(v, s.id).length })).filter((x) => x.n >= th).sort((a, b) => b.n - a.n);
  const openHeavy = v.students.map((s) => ({ s, n: openItems(v, s.id).length })).filter((x) => x.n >= 3).sort((a, b) => b.n - a.n);
  const unscheduled = v.students.filter((s) => openItems(v, s.id).length > 0 && upcoming(v, s.id).length === 0);
  const mine = v.sessions.filter((s) => me && s.createdBy === me.name && s.createdAt > Date.now() - 86_400_000).length;
  const five = wc !== "compact";

  if (!v.status.loaded) return <Loading />;

  const soon = (
    <Section title="곧 올 학생" count={next.length} action={<a className="link" href="#/today">당일 클리닉 전체 <Icon name="right" size={16} /></a>}>
      {next.length === 0 ? <Empty>오늘 남은 일정이 없습니다.</Empty> : (
        <ul className="list">
          {next.slice(0, LIMIT).map((s) => {
            const st = sm.get(s.studentId)!;
            const open = itemsFor(v, s).filter((i) => !i.done).length;
            return (
              <li key={s.id}>
                <a className="row-card" href="#/today">
                  <span className="time">{s.time}</span>
                  <StudentName st={st} />
                  <span className="grow" />
                  <span className="muted nowrap">시험지 {open}</span>
                </a>
              </li>
            );
          })}
          {next.length > LIMIT ? <li><a className="link more" href="#/today">전체 {next.length}명 보기 <Icon name="right" size={16} /></a></li> : null}
        </ul>
      )}
    </Section>
  );

  const noNext = (
    <Section title="다음 일정이 없는 학생" count={unscheduled.length} warn>
      {unscheduled.length === 0 ? <Empty>남은 클리닉이 있는 학생은 모두 일정이 잡혀 있습니다.</Empty> : (
        <ul className="list">
          {unscheduled.slice(0, LIMIT).map((st) => (
            <li key={st.id}>
              <a className="row-card" href={`#/register?student=${encodeURIComponent(st.id)}`}>
                <StudentName st={st} />
                <span className="grow" />
                <span className="muted nowrap">남은 {openItems(v, st.id).length}</span>
                <span className="cta nowrap">일정 잡기</span>
              </a>
            </li>
          ))}
          {unscheduled.length > LIMIT ? <li><a className="link more" href="#/students">전체 {unscheduled.length}명 <Icon name="right" size={16} /></a></li> : null}
        </ul>
      )}
    </Section>
  );

  const warnSec = (
    <Section title={`경고카드 ${th}장 이상`} count={warnHeavy.length} warn>
      {warnHeavy.length === 0 ? <Empty>기준을 넘은 학생이 없습니다.</Empty> : (
        <ul className="list">
          {warnHeavy.slice(0, LIMIT).map(({ s, n }) => (
            <li key={s.id}>
              <a className="row-card" href={`#/students/${encodeURIComponent(s.id)}`}>
                <StudentName st={s} />
                <span className="grow" />
                <Pill color="var(--color-danger)">경고 {n}장</Pill>
              </a>
            </li>
          ))}
          {warnHeavy.length > LIMIT ? <li><a className="link more" href="#/board">전체 {warnHeavy.length}명 <Icon name="right" size={16} /></a></li> : null}
        </ul>
      )}
    </Section>
  );

  const openSec = (
    <Section title="남은 클리닉 3개 이상" count={openHeavy.length}>
      {openHeavy.length === 0 ? <Empty>없습니다.</Empty> : (
        <ul className="list">
          {openHeavy.slice(0, LIMIT).map(({ s, n }) => (
            <li key={s.id}>
              <a className="row-card" href={`#/students/${encodeURIComponent(s.id)}`}>
                <StudentName st={s} />
                <span className="grow" />
                <Pill color="var(--color-status-partial)">남은 {n}개</Pill>
              </a>
            </li>
          ))}
          {openHeavy.length > LIMIT ? <li><a className="link more" href="#/students">전체 {openHeavy.length}명 <Icon name="right" size={16} /></a></li> : null}
        </ul>
      )}
    </Section>
  );

  return (
    <div className="page dash">
      <p className="muted lead">
        {d.getMonth() + 1}월 {d.getDate()}일 {DOW[dowValue(td) - 1]}요일 · {me?.name ? `${me.name}님` : "로그인됨"} · 최근 24시간 내가 등록한 일정 {mine}건
      </p>
      <div className={"stats" + (five ? " stats-5" : "")}>
        <StatTile n={stt.total} label="오늘 등원 예정" />
        <StatTile n={stt.done} label="완료" color="var(--color-status-done)" />
        <StatTile n={stt.partial} label="미완료" color="var(--color-status-partial)" />
        <StatTile n={stt.noshow} label="노쇼" color="var(--color-status-noshow)" />
        {five ? <StatTile n={stt.waiting} label="아직 안 옴" color="var(--color-status-plan)" /> : null}
      </div>
      {wc === "large" ? (
        <div className="cols cols-3">
          <div>{soon}</div>
          <div>{noNext}{warnSec}</div>
          <div>{openSec}</div>
        </div>
      ) : wc === "compact" ? (
        <div className="cols">{soon}{noNext}{warnSec}{openSec}</div>
      ) : (
        <div className="cols cols-2">
          <div>{soon}</div>
          <div>{noNext}{warnSec}{openSec}</div>
        </div>
      )}
    </div>
  );
}

export function Loading() {
  return (
    <div className="page">
      <p className="muted lead">데이터를 불러오는 중입니다…</p>
      <div className="skeleton" />
      <div className="skeleton" />
    </div>
  );
}
